"use client";

import { useEffect } from "react";

import { isRecoverableClientStartupError } from "@/lib/client-error-recovery";

const RECOVERY_STORAGE_KEY = "sendblue.client-startup-recovery-at";
const RECOVERY_COOLDOWN_MS = 60_000;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const recoverable = isRecoverableClientStartupError(error);

  useEffect(() => {
    if (!recoverable) return;

    try {
      const now = Date.now();
      const previousAttempt = Number(
        window.sessionStorage.getItem(RECOVERY_STORAGE_KEY),
      );
      if (
        Number.isFinite(previousAttempt) &&
        now - previousAttempt < RECOVERY_COOLDOWN_MS
      ) {
        return;
      }
      window.sessionStorage.setItem(RECOVERY_STORAGE_KEY, String(now));
    } catch {
      // If session storage is unavailable, avoid risking a reload loop.
      return;
    }

    window.location.reload();
  }, [recoverable]);

  return (
    <html lang="en">
      <body>
        <main className="flex min-h-screen items-center justify-center bg-whispering-white px-6">
          <div className="w-full max-w-md rounded-xl border border-grey-neutral bg-white p-8 text-center shadow-sm">
            <h1 className="text-xl font-semibold text-ttwilight-navy">
              {recoverable ? "Refreshing Sendblue…" : "Something went wrong"}
            </h1>
            <p className="mt-3 text-sm text-grey-subtext">
              {recoverable
                ? "The app changed while this tab was open. We’re loading the latest version now."
                : "We couldn’t load this page. Try it again, or reload the tab if the problem continues."}
            </p>
            <button
              type="button"
              onClick={recoverable ? () => window.location.reload() : reset}
              className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              {recoverable ? "Reload now" : "Try again"}
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
